import Phaser from 'phaser';
import type { PerkDefinition, PerkCategory } from '@snake-royalle/shared';
import { disableMenuScroll } from '../utils/menu-scroll.js';
import { sfx } from '../audio/SFX.js';
import { playContext as playMusic } from '../audio/Music.js';
import { PERK_SFX, loadSfxGroup } from '../audio/sfx-groups.js';
import {
  isPaymentRequired,
  formatEntryFee,
  formatLamportsAsSOL,
  payEntryFee,
  payEntryFeeSOL,
  payEntryFeeNative,
  verifyAndGetEntryToken,
  getDevEntryToken,
  getInstantDevToken,
  fetchPaymentQuote,
  fetchPaymentConfigStatus,
  nativeQuoteIsAvailable,
  InsufficientNativeBalanceError,
  WalletMismatchError,
  type PaymentQuote,
  type EntryTokenResult,
  type StakeMultiplier,
  IS_DEV_BUILD,
  isFreePlayEnabled,
  PAYMENT_CONFIG,
} from '../payments/entry-fee.js';
import { PaymentOverlay, type CoinKind, type StakeMultiplierValue } from '../effects/PaymentOverlay.js';
import { connectWallet, getSavedWallet, getWalletSessionToken } from '../auth/wallet-auth.js';
import { track } from '../analytics.js';

const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

/** Feature flag — the penguin Tournament mode is temporarily disabled. When
 *  false, the TOURNAMENT cluster on this screen is hidden and only the
 *  centered ARENA/DEAL flow is shown. The tournament server room, routes and
 *  DB stay intact; flip this back to true (and unhide the leaderboard tab in
 *  index.html) to re-enable. */
const TOURNAMENT_ENABLED = false;

/** Format a raw token amount into "X.X SYMBOL" with the given decimals.
 *  Used for the perk-select prize-pool label. The pool is denominated in
 *  USDC on devnet (decimals=6) and the native token on mainnet (decimals
 *  varies per env). */
function formatPool(rawStr: string | number, symbol: string, decimals: number): string {
  try {
    const raw = BigInt(rawStr || 0);
    const divisor = 10n ** BigInt(decimals);
    const whole = raw / divisor;
    const frac = (raw % divisor).toString().padStart(decimals, '0').slice(0, 1);
    return `${whole.toLocaleString()}.${frac} ${symbol}`;
  } catch { return `0.0 ${symbol}`; }
}

const CATEGORY_COLORS: Record<PerkCategory, number> = {
  ECONOMY: 0xffdd00,
  PVP: 0xff4444,
  MOVEMENT: 0x00ccff,
  HIGH_RISK: 0xff00ff,
};

/** Deep desaturated tint for the card body — keeps the category readable at a glance */
const CATEGORY_BODY_TINTS: Record<PerkCategory, number> = {
  ECONOMY:   0x1a1408, // warm gold-brown
  PVP:       0x1c0a0a, // dark crimson
  MOVEMENT:  0x081420, // cool deep blue
  HIGH_RISK: 0x14081a, // dark royal purple
};

const CATEGORY_LABELS: Record<PerkCategory, string> = {
  ECONOMY: 'ECONOMY',
  PVP: 'PVP',
  MOVEMENT: 'SPEED',
  HIGH_RISK: 'HIGH RISK',
};

/** Casino-style jackpot perk selection with Snake Crupier */
export class PerkSelectScene extends Phaser.Scene {
  private perks: PerkDefinition[] = [];
  private cards: Phaser.GameObjects.Container[] = [];
  private selectedIndex: number = -1;
  private dealt: boolean = false;
  private entryToken: string | null = null;  // set by payment flow before cards are dealt
  private demoMode: boolean = false;
  /** Tournament mode — chosen on this screen (credit entry, one life,
   *  separate score). Mutually exclusive with the paid-arena path. */
  private tournamentMode: boolean = false;
  private tournamentTicket: string | null = null;

  constructor() {
    super({ key: 'PerkSelectScene' });
  }

  init(data: { perks: PerkDefinition[]; demoMode?: boolean }): void {
    this.perks = data.perks;
    this.selectedIndex = -1;
    this.cards = [];
    this.dealt = false;
    this.demoMode = !!data.demoMode;
    this.entryToken = null;
    this.tournamentMode = false;
    this.tournamentTicket = null;
  }

  preload(): void {
    if (!this.textures.exists('crupier')) {
      this.load.image('crupier', 'assets/sprites/menus/snakecrupier.webp');
    }
    if (!this.textures.exists('card_frame')) {
      this.load.image('card_frame', 'assets/sprites/cards/card_frame.webp');
    }
    if (!this.textures.exists('pengcup')) {
      this.load.image('pengcup', 'assets/sprites/tournament/pengcup.png');
    }
    const perkIds = ['gold_rush', 'midas_touch', 'predator', 'assassin', 'nitro', 'ghost', 'glass_cannon', 'all_in'];
    for (const id of perkIds) {
      const key = `perk_icon_${id}`;
      if (this.textures.exists(key)) continue;
      this.load.image(key, `assets/sprites/cards/${id}.png`);
    }
    loadSfxGroup(this, PERK_SFX);
  }

  create(): void {
    disableMenuScroll();
    this.scene.stop('GameScene');
    this.scene.stop('HUDScene');
    playMusic(this.game, 'menu');
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.fadeIn(300);

    const { width, height } = this.scale;
    const cx = width / 2;

    // === BACKGROUND ===
    this.add.rectangle(cx, height / 2, width, height, 0x000000).setDepth(0);

    // === CRUPIER IMAGE — fit height, center horizontally, no stretch ===
    if (this.textures.exists('crupier')) {
      const crupier = this.add.image(cx, height / 2, 'crupier')
        .setOrigin(0.5, 0.5)
        .setAlpha(0)
        .setDepth(1);

      // Scale to fit HEIGHT (keeps aspect ratio, may leave black bars on sides)
      const fitScale = height / crupier.height;
      crupier.setScale(fitScale);

      // Dark overlay for readability
      this.add.rectangle(cx, height / 2, width, height, 0x000000, 0.35).setDepth(2);

      // Fade in
      this.tweens.add({
        targets: crupier,
        alpha: 0.95,
        duration: 1000,
        ease: 'Power2',
      });
    }

    // === GOLD DUST PARTICLES ===
    if (!this.textures.exists('perk_dust')) {
      const dustGfx = this.add.graphics();
      dustGfx.fillStyle(0xffd700, 1);
      dustGfx.fillRect(0, 0, 2, 2);
      dustGfx.generateTexture('perk_dust', 2, 2);
      dustGfx.destroy();
    }

    this.add.particles(width / 2, height / 2, 'perk_dust', {
      x: { min: -width / 2, max: width / 2 },
      y: { min: -height / 2, max: -height / 2 },
      scale: { min: 0.5, max: 1.5 },
      alpha: { start: 0.8, end: 0 },
      lifespan: { min: 2000, max: 5000 },
      speed: { min: 15, max: 40 },
      angle: { min: 80, max: 100 },
      frequency: 40,
      quantity: 3,
      tint: [0xffd700, 0xffffff, 0x9d00ff, 0xffaa00],
      blendMode: 'ADD',
    }).setDepth(3).setScrollFactor(0);

    // === TITLE ===
    const title = this.add.text(cx, height * 0.06, 'CHOOSE YOUR FATE', {
      fontFamily: 'monospace',
      fontSize: '40px',
      color: '#ffd700',
      stroke: '#aa6600',
      strokeThickness: 4,
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(10).setScale(2);

    this.tweens.add({
      targets: title,
      alpha: 1,
      scale: 1,
      y: height * 0.08,
      duration: 500,
      delay: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        sfx(this, 'title_slam');
        this.cameras.main.shake(80, 0.005);
      },
    });

    // === DEAL BUTTON — glow on the card deck in crupier's hands ===
    const deckY = height * 0.62;
    const deckGlow = this.add.graphics().setDepth(5);

    // Pulsing glow — strong, on the deck
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        if (this.dealt) { deckGlow.clear(); return; }
        const v = (tween.getValue() ?? 0) as number;
        deckGlow.clear();

        // Outer gold glow
        const rings = 6;
        for (let r = rings; r >= 0; r--) {
          const progress = r / rings;
          const alpha = (1 - progress) * 0.35 * (0.6 + v * 0.4);
          const radius = (100 + v * 25) * progress;
          deckGlow.fillStyle(0xffd700, alpha);
          deckGlow.fillCircle(cx, deckY, radius);
        }

        // Inner white-hot core
        for (let r = 3; r >= 0; r--) {
          const progress = r / 3;
          const alpha = (1 - progress) * 0.25 * (0.5 + v * 0.5);
          const radius = (35 + v * 10) * progress;
          deckGlow.fillStyle(0xffffff, alpha);
          deckGlow.fillCircle(cx, deckY, radius);
        }
      },
    });

    // === Two ways to play, flanking the shared deck ===
    //   ARENA (left)      — real-money entry, the existing paid flow.
    //   TOURNAMENT (right) — credit entry (2/day), one life, separate score.
    // The dealer sits center; whichever side is picked deals from center.
    // Demo is a free, no-wallet try — TOURNAMENT (credits + wallet) makes no
    // sense there, so demo shows ONLY the centered ARENA/DEAL cluster.
    const isDemo = this.demoMode;
    // Tournament is shown only for non-demo sessions AND while the mode is
    // enabled. When hidden, the ARENA cluster centers like it does in demo.
    const showTournament = !isDemo && TOURNAMENT_ENABLED;
    const ctaY = deckY + 16;
    const xArena = showTournament ? cx - width * 0.18 : cx;
    const xTour  = cx + width * 0.18;

    const mkTitle = (x: number, label: string, color: string) =>
      this.add.text(x, ctaY - 28, label, {
        fontFamily: 'monospace', fontSize: '20px', color,
        stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0).setDepth(10);
    const mkSub = (x: number, text: string, color: string) =>
      this.add.text(x, ctaY, text, {
        fontFamily: 'monospace', fontSize: '16px', color,
        stroke: '#000', strokeThickness: 4,
      }).setOrigin(0.5).setAlpha(0).setDepth(10).setInteractive({ useHandCursor: true });
    const mkBtn = (x: number, text: string, color: string) =>
      this.add.text(x, ctaY + 36, text, {
        fontFamily: 'monospace', fontSize: '26px', color,
        stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0).setDepth(10).setInteractive({ useHandCursor: true });

    // --- ARENA cluster ---
    const arenaTitle = mkTitle(xArena, 'ARENA', '#ffd700');
    const poolText = mkSub(xArena, 'PRIZE POOL  —', '#ffd166');
    const dealText = mkBtn(xArena, '[ DEAL ]', '#ffd700');
    const arenaCluster = [arenaTitle, poolText, dealText];

    // --- TOURNAMENT cluster (skipped in demo and while the mode is disabled) ---
    let tourCluster: Phaser.GameObjects.GameObject[] = [];
    let tourTitle: Phaser.GameObjects.Text | null = null;
    let creditsText: Phaser.GameObjects.Text | null = null;
    let enterText: Phaser.GameObjects.Text | null = null;
    if (showTournament) {
      // Cup image above the title — the PNG is already a borderless circular
      // trophy, so just place it (no ring/frame).
      const badgeY = ctaY - 88;
      let cupImg: Phaser.GameObjects.Image | null = null;
      if (this.textures.exists('pengcup')) {
        cupImg = this.add.image(xTour, badgeY, 'pengcup').setDepth(11).setAlpha(0);
        const baseScale = 76 / Math.max(cupImg.width, cupImg.height);
        cupImg.setScale(baseScale);
        this.tweens.add({
          targets: cupImg, scale: baseScale * 1.08, duration: 900,
          yoyo: true, repeat: -1, delay: 1400, ease: 'Sine.easeInOut',
        });
      }
      tourTitle = mkTitle(xTour, 'TOURNAMENT', '#66ccff');
      creditsText = mkSub(xTour, 'CREDITS  —', '#9fd3ff');
      enterText = mkBtn(xTour, '[ ENTER ]', '#66ccff');
      tourCluster = [tourTitle, creditsText, enterText, ...(cupImg ? [cupImg] : [])];
    }

    this.tweens.add({ targets: [...arenaCluster, ...tourCluster], alpha: 0.95, duration: 400, delay: 900 });
    this.tweens.add({
      targets: [dealText, ...(enterText ? [enterText] : [])], scale: 1.08, duration: 600,
      yoyo: true, repeat: -1, delay: 1400, ease: 'Sine.easeInOut',
    });

    // Arena prize pool (existing endpoint). "—" on failure is graceful.
    fetch(`${SERVER_HTTP_URL}/api/arena/current`)
      .then((r) => r.json())
      .then((data) => {
        const raw = data?.poolToken ?? data?.poolUsdcRaw ?? '0';
        const symbol = data?.poolSymbol ?? 'USDC';
        const decimals = data?.poolDecimals ?? 6;
        poolText.setText(`PRIZE POOL  ${formatPool(raw, symbol, decimals)}`);
      })
      .catch(() => { /* keep the em-dash */ });

    // Tournament daily credits (needs a wallet session; "—" otherwise).
    if (creditsText) void this.refreshTournamentCredits(creditsText);

    const hover = (t: Phaser.GameObjects.Text, base: string, on: boolean) => {
      if (this.dealt) return;
      t.setColor(on ? '#ffffff' : base);
    };
    const wireHover = (t: Phaser.GameObjects.Text, base: string) => {
      t.on('pointerover', () => hover(t, base, true));
      t.on('pointerout',  () => hover(t, base, false));
    };
    wireHover(dealText, '#ffd700');
    wireHover(poolText, '#ffd166');
    if (enterText) wireHover(enterText, '#66ccff');
    if (creditsText) wireHover(creditsText, '#9fd3ff');

    const fadeOut = (objs: Phaser.GameObjects.GameObject[]) =>
      this.tweens.add({ targets: objs, alpha: 0, duration: 250 });

    const triggerArena = () => {
      if (this.dealt) return;
      this.dealt = true;
      this.tournamentMode = false;
      fadeOut([...tourCluster, poolText, arenaTitle]);
      this.payThenDeal(cx, deckY, dealText, deckGlow);
    };
    // Re-show both CTAs + clear the dealing lock (used when tournament
    // entry fails, e.g. no credits — let the player pick ARENA or retry).
    const restoreChoice = () => {
      this.dealt = false;
      this.tournamentMode = false;
      this.tweens.add({ targets: [...arenaCluster, ...tourCluster], alpha: 0.95, duration: 250 });
    };
    const triggerTournament = () => {
      if (this.dealt || !enterText || !creditsText || !tourTitle) return;
      this.dealt = true;
      this.tournamentMode = true;
      // Fade the whole tournament cluster (incl. the cup badge, which was
      // staying stuck on screen) + arena — keep only ENTER, which the deal
      // animation destroys.
      const tourEnter = enterText;
      fadeOut([...arenaCluster, ...tourCluster.filter((o) => o !== tourEnter)]);
      void this.tournamentEnterThenDeal(cx, deckY, enterText, deckGlow, creditsText, restoreChoice);
    };

    dealText.on('pointerdown', triggerArena);
    poolText.on('pointerdown', triggerArena);
    enterText?.on('pointerdown', triggerTournament);
    creditsText?.on('pointerdown', triggerTournament);

    // Back to menu button
    const backBtn = this.add.text(cx, height * 0.95, 'Back to Menu', {
      fontFamily: 'monospace', fontSize: '12px', color: '#444455',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

    backBtn.on('pointerover', () => backBtn.setColor('#888899'));
    backBtn.on('pointerout', () => backBtn.setColor('#444455'));
    backBtn.on('pointerdown', () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    });
  }

  /** Deal cards from the deck position */
  private dealCards(
    cx: number,
    deckY: number,
    dealText: Phaser.GameObjects.Text,
    deckGlow: Phaser.GameObjects.Graphics
  ): void {
    const { width, height } = this.scale;

    // Flash + shake on deal
    sfx(this, 'deal_button_press');
    this.cameras.main.flash(200, 255, 215, 0, false);
    this.cameras.main.shake(150, 0.008);

    // Remove deal button
    this.tweens.add({
      targets: dealText,
      alpha: 0,
      scale: 2,
      duration: 200,
      onComplete: () => dealText.destroy(),
    });

    // Clear glow
    deckGlow.clear();

    // Burst particles from deck
    if (!this.textures.exists('deal_burst')) {
      const burstGfx = this.add.graphics();
      burstGfx.fillStyle(0xffd700, 1);
      burstGfx.fillRect(0, 0, 3, 3);
      burstGfx.generateTexture('deal_burst', 3, 3);
      burstGfx.destroy();
    }

    const burst = this.add.particles(cx, deckY, 'deal_burst', {
      speed: { min: 40, max: 120 },
      angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 500,
      quantity: 20,
      tint: [0xffd700, 0xffffff, 0x9d00ff],
      blendMode: 'ADD',
      emitting: false,
    }).setDepth(8);
    burst.explode(20);
    this.time.delayedCall(600, () => burst.destroy());

    // === DEAL CARDS from deck position ===
    const cardWidth = Math.min(155, (width - 80) / 3.5);
    const cardHeight = cardWidth * 1.55; // Magic card proportions
    const cardSpacing = cardWidth + 20;
    const startX = cx - cardSpacing;
    const cardY = height * 0.6;

    this.perks.forEach((perk, i) => {
      const cardX = startX + i * cardSpacing;

      const card = this.createPerkCard(perk, cardX, cardY, cardWidth, cardHeight, i);
      this.cards.push(card);
      card.setDepth(10);

      // Start at deck position, stacked and small
      card.setPosition(cx, deckY);
      card.setScale(0.3);
      card.setAlpha(0.8);
      card.setAngle(-15 + i * 15);

      // Fly out to position, then float
      this.tweens.add({
        targets: card,
        x: cardX,
        y: cardY,
        scale: 1,
        alpha: 1,
        angle: 0,
        duration: 450,
        delay: 200 + i * 250,
        ease: 'Back.easeOut',
        onStart: () => {
          sfx(this, 'card_deal');
          this.cameras.main.shake(40, 0.003);
        },
        onComplete: () => {
          sfx(this, 'card_land');
          // Floating — vertical bob
          this.tweens.add({
            targets: card,
            y: cardY - 5,
            duration: 1500 + i * 200,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
          // Floating — horizontal sway (subtle)
          this.tweens.add({
            targets: card,
            x: cardX + 3,
            duration: 2000 + i * 300,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
          // Floating — slight rotation
          this.tweens.add({
            targets: card,
            angle: 1.5,
            duration: 2500 + i * 400,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
    });

    // "CHOOSE" prompt after cards are dealt
    const chooseDelay = 200 + 3 * 250 + 600;
    const chooseText = this.add.text(cx, height * 0.92, '▼  CHOOSE YOUR PERK  ▼', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffd700',
    }).setOrigin(0.5).setAlpha(0).setDepth(10);

    this.tweens.add({
      targets: chooseText, alpha: 1, duration: 400, delay: chooseDelay,
    });
    this.tweens.add({
      targets: chooseText, alpha: 0.4, duration: 800,
      yoyo: true, repeat: -1, delay: chooseDelay + 400, ease: 'Sine.easeInOut',
    });
  }

  private createPerkCard(
    perk: PerkDefinition, x: number, y: number, w: number, h: number, index: number
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const color = CATEGORY_COLORS[perk.category];
    const categoryLabel = CATEGORY_LABELS[perk.category];
    const colorHex = `#${color.toString(16).padStart(6, '0')}`;

    // === CARD FRAME (real sprite, tinted by category) ===

    // Layered drop shadow — four offsets fake a soft blur with real weight
    const shadowFar2 = this.add.rectangle(12, 18, w + 10, h + 10, 0x000000, 0.18);
    const shadowFar  = this.add.rectangle(8,  12, w + 6,  h + 6,  0x000000, 0.30);
    const shadowMid  = this.add.rectangle(5,  8,  w + 2,  h + 2,  0x000000, 0.45);
    const shadowNear = this.add.rectangle(2,  4,  w,      h,      0x000000, 0.65);

    // Card frame sprite — scaled to fit card dimensions, tinted by category
    const frameSprite = this.add.image(0, 0, 'card_frame')
      .setDisplaySize(w, h)
      .setTint(color);

    // Dark fill behind content — tinted toward the category so the body
    // reads as that category at a glance, not just from the border color.
    const bodyTint = CATEGORY_BODY_TINTS[perk.category];
    const innerFill = this.add.rectangle(0, 0, w - 12, h - 12, bodyTint, 0.96);

    // Paper-grain noise overlay — subtle texture so the body isn't flat
    const paper = this.add.image(0, 0, this.getPaperTexture())
      .setDisplaySize(w - 12, h - 12)
      .setBlendMode(Phaser.BlendModes.OVERLAY)
      .setAlpha(0.18);

    // Inner printed border (Magic/Hearthstone style) — thin colored stroke
    // inset a few px from the cardstock edge.
    const innerBorder = this.add.rectangle(0, 0, w - 20, h - 20, 0x000000, 0)
      .setStrokeStyle(1, color, 0.55);

    // Bevel chamfer — 1px highlight on top, 1px shadow on bottom. Fakes
    // the visible "thickness" of the cardstock seen from a slight angle.
    const bevelTop = this.add.rectangle(0, -h / 2 + 6,  w - 14, 1, 0xffffff, 0.30);
    const bevelBot = this.add.rectangle(0,  h / 2 - 6,  w - 14, 1, 0x000000, 0.55);

    // --- TOP: Icon area (the artwork window) ---
    // Positioned in the upper portion of the card frame's image area
    const imgAreaH = h * 0.42;
    const imgAreaY = -h / 2 + 10 + imgAreaH / 2;
    const imgAreaW = w - 18;

    // No solid background here — the icon (transparent PNG) sits directly
    // on the tinted card body. The vignette below fades the body's edges
    // around the artwork so the icon feels framed without a hard rectangle.
    const vignette = this.add.image(0, imgAreaY, this.getVignetteTexture())
      .setDisplaySize(imgAreaW, imgAreaH);

    // Pixel art icon (square, fits inside the image area)
    const iconSize = Math.min(imgAreaH - 8, w - 26);
    const iconKey = `perk_icon_${perk.id}`;
    const iconImg: Phaser.GameObjects.Image | Phaser.GameObjects.Text = this.textures.exists(iconKey)
      ? this.add.image(0, imgAreaY, iconKey).setDisplaySize(iconSize, iconSize)
      : this.add.text(0, imgAreaY, '?', {
          fontFamily: 'monospace', fontSize: '40px', color: colorHex,
        }).setOrigin(0.5);
    const iconBaseScaleX = iconImg.scaleX;
    const iconBaseScaleY = iconImg.scaleY;

    // Subtle glow behind icon (matches category color, low intensity so the art reads)
    const iconGlow = this.add.graphics();
    for (let r = 4; r >= 0; r--) {
      const progress = r / 4;
      iconGlow.fillStyle(color, (1 - progress) * 0.08);
      iconGlow.fillCircle(0, imgAreaY, 38 * progress);
    }

    // --- MIDDLE: Name ribbon (sits on the frame's middle bar) ---
    const nameBarY = imgAreaY + imgAreaH / 2 + 16;
    const nameBg = this.add.rectangle(0, nameBarY, w - 14, 22, color, 0.25);
    const nameText = this.add.text(0, nameBarY, perk.name.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '11px', color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // --- BOTTOM: Description text ---
    const descY = nameBarY + 32;
    const descText = this.add.text(0, descY, perk.description, {
      fontFamily: 'monospace', fontSize: '10px', color: '#bbbbcc',
      wordWrap: { width: w - 24 }, align: 'center', lineSpacing: 3,
    }).setOrigin(0.5);

    // --- FOOTER: Category + rarity ---
    const catY = h / 2 - 16;
    const catText = this.add.text(0, catY, categoryLabel, {
      fontFamily: 'monospace', fontSize: '9px', color: colorHex,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Rarity dots just above footer
    const dotsY = catY - 14;
    for (let i = 0; i < perk.rarity; i++) {
      const dotX = (i - (perk.rarity - 1) / 2) * 10;
      container.add(this.add.circle(dotX, dotsY, 3, color));
    }

    container.add([
      shadowFar2, shadowFar, shadowMid, shadowNear,
      frameSprite, innerFill, paper, innerBorder,
      iconGlow, iconImg, vignette,
      nameBg, nameText, descText, catText,
      bevelTop, bevelBot,
    ]);

    // === HIT ZONE (covers entire card) ===
    const hitZone = this.add.rectangle(0, 0, w, h, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    container.add(hitZone);

    // Glow graphics (behind card, hidden by default)
    const hoverGlow = this.add.graphics();
    hoverGlow.setAlpha(0);
    for (let r = 6; r >= 0; r--) {
      const progress = r / 6;
      hoverGlow.fillStyle(color, (1 - progress) * 0.12);
      hoverGlow.fillCircle(0, 0, (Math.max(w, h) * 0.7) * progress);
    }
    container.addAt(hoverGlow, 0); // Behind everything

    hitZone.on('pointerover', () => {
      if (this.selectedIndex >= 0) return;
      sfx(this, 'card_hover');
      // Subtle lift + scale (Slay the Spire style)
      this.tweens.add({
        targets: container, scaleX: 1.1, scaleY: 1.1, duration: 200, ease: 'Power2',
      });
      // Glow appears behind
      this.tweens.add({ targets: hoverGlow, alpha: 1, duration: 200 });
      // Slightly brighten the frame (not white — just lighter tint)
      const lighterColor = this.lightenColor(color, 0.4);
      frameSprite.setTint(lighterColor);
      // Icon pulses slightly (relative to its post-displaySize base scale)
      this.tweens.add({ targets: iconImg, scaleX: iconBaseScaleX * 1.08, scaleY: iconBaseScaleY * 1.08, duration: 200 });
    });

    hitZone.on('pointerout', () => {
      if (this.selectedIndex >= 0) return;
      this.tweens.add({
        targets: container, scaleX: 1, scaleY: 1, duration: 200,
      });
      this.tweens.add({ targets: hoverGlow, alpha: 0, duration: 200 });
      frameSprite.setTint(color);
      this.tweens.add({ targets: iconImg, scaleX: iconBaseScaleX, scaleY: iconBaseScaleY, duration: 200 });
    });

    hitZone.on('pointerdown', () => {
      if (this.selectedIndex >= 0) return;
      this.selectPerk(index);
    });

    return container;
  }

  private selectPerk(index: number): void {
    sfx(this, 'card_select');
    this.selectedIndex = index;
    const selectedPerk = this.perks[index];
    const color = CATEGORY_COLORS[selectedPerk.category];
    const { width, height } = this.scale;

    const selectedCard = this.cards[index];
    this.tweens.add({ targets: selectedCard, scaleX: 1.2, scaleY: 1.2, y: height * 0.5, duration: 300, ease: 'Back.easeOut' });

    this.cameras.main.flash(250, 255, 215, 0, false);
    this.cameras.main.shake(200, 0.012);

    this.cards.forEach((card, i) => {
      if (i !== index) {
        this.tweens.add({ targets: card, alpha: 0, y: height + 100, scale: 0.5, duration: 400, delay: 100, ease: 'Power2' });
      }
    });

    const selectedText = this.add.text(width / 2, height * 0.82, `${selectedPerk.name.toUpperCase()} SELECTED!`, {
      fontFamily: 'monospace', fontSize: '22px',
      color: `#${color.toString(16).padStart(6, '0')}`,
      stroke: '#000000', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(20);

    this.tweens.add({ targets: selectedText, alpha: 1, duration: 300, delay: 300,
      onComplete: () => sfx(this, 'perk_confirmed'),
    });

    if (!this.textures.exists('select_burst')) {
      const burstGfx = this.add.graphics();
      burstGfx.fillStyle(0xffd700, 1);
      burstGfx.fillRect(0, 0, 3, 3);
      burstGfx.generateTexture('select_burst', 3, 3);
      burstGfx.destroy();
    }

    const burst = this.add.particles(width / 2, height * 0.5, 'select_burst', {
      speed: { min: 50, max: 150 }, angle: { min: 0, max: 360 },
      scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 },
      lifespan: 600, quantity: 30, tint: [color, 0xffd700, 0xffffff],
      blendMode: 'ADD', emitting: false,
    }).setDepth(15);
    burst.explode(30);

    // Payment already happened before cards were dealt — just hand off to GameScene
    this.time.delayedCall(1500, () => {
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GameScene', {
          selectedPerk,
          entryToken: this.entryToken,
          demoMode: this.demoMode,
          tournamentMode: this.tournamentMode,
          tournamentTicket: this.tournamentTicket,
        });
      });
    });
  }

  /** Resolve the logged-in wallet (saved session). Returns address+token,
   *  or null if not logged in. */
  private resolveWalletSession(): { address: string; token: string } | null {
    const address = (window as any).__currentUser?.walletAddress || getSavedWallet()?.address;
    if (!address) return null;
    const token = getWalletSessionToken(address);
    if (!token) return null;
    return { address, token };
  }

  /** Fetch + display the player's remaining daily tournament credits. */
  private async refreshTournamentCredits(label: Phaser.GameObjects.Text): Promise<void> {
    const session = this.resolveWalletSession();
    if (!session) { label.setText('CREDITS  LOGIN'); return; }
    try {
      const res = await fetch(
        `${SERVER_HTTP_URL}/api/tournament/credits?wallet=${encodeURIComponent(session.address)}`,
        { headers: { Authorization: `Bearer ${session.token}` } },
      );
      if (!res.ok) return;
      const data = await res.json();
      label.setText(`CREDITS  ${data.remaining ?? 0}/${data.max ?? 2}`);
    } catch { /* keep the em-dash */ }
  }

  /**
   * Consume a tournament credit (server-authoritative), get a ticket, then
   * deal the server-rolled perks. On failure (no credits / not logged in)
   * restores the choice screen instead of dealing.
   */
  private async tournamentEnterThenDeal(
    cx: number,
    deckY: number,
    enterText: Phaser.GameObjects.Text,
    deckGlow: Phaser.GameObjects.Graphics,
    creditsText: Phaser.GameObjects.Text,
    restore: () => void,
  ): Promise<void> {
    // Need a wallet session. Prompt connect if not logged in.
    let session = this.resolveWalletSession();
    if (!session) {
      const res = await connectWallet();
      if (res) session = { address: res.address, token: res.sessionToken };
    }
    if (!session) {
      creditsText.setText('CONNECT WALLET').setAlpha(1).setColor('#ff6666');
      restore();
      return;
    }

    try {
      const resp = await fetch(`${SERVER_HTTP_URL}/api/tournament/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
        body: JSON.stringify({
          walletAddress: session.address,
          playerName: (window as any).__currentUser?.name ?? undefined,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const msg = err?.reason === 'no_credits' ? 'NO CREDITS TODAY' : 'TOURNAMENT UNAVAILABLE';
        creditsText.setText(msg).setAlpha(1).setColor('#ff6666');
        restore();
        return;
      }
      const data = await resp.json();
      this.tournamentTicket = data.ticket;
      if (Array.isArray(data.perkOptions) && data.perkOptions.length > 0) {
        this.perks = data.perkOptions;
      }
      this.dealCards(cx, deckY, enterText, deckGlow);
    } catch {
      creditsText.setText('NETWORK ERROR').setAlpha(1).setColor('#ff6666');
      restore();
    }
  }

  /**
   * Pays the entry fee (if required), then triggers card reveal.
   * Failure path: show error in overlay, return to MainMenuScene after 3s.
   * Dev mode (ENTRY_FEE_AMOUNT=0): server still issues a token, no on-chain tx.
   */
  private async payThenDeal(
    cx: number,
    deckY: number,
    dealText: Phaser.GameObjects.Text,
    deckGlow: Phaser.GameObjects.Graphics
  ): Promise<void> {
    const overlay = new PaymentOverlay();
    const playerName = (window as any).__currentUser?.name || 'Player';

    const onPaid = (result: EntryTokenResult) => {
      this.entryToken = result.entryToken;
      if (result.perkOptions.length > 0) {
        this.perks = result.perkOptions;
      }
      overlay.hide();
      this.dealCards(cx, deckY, dealText, deckGlow);
    };

    const onFailure = (err: any) => {
      const msg = err?.message || 'Unknown error';
      console.error('Entry fee payment failed:', err);
      overlay.setStep('failed');

      // Special-case: not enough native tokens. The pre-flight check fired
      // BEFORE signing, so no gas was wasted. Render the buy URL as a
      // clickable button — a user click is also more reliable than
      // window.open (browsers block auto-popups).
      if (err instanceof InsufficientNativeBalanceError) {
        overlay.setStatusWithLink(
          msg,
          'error',
          '',
          { href: err.buyUrl(), label: `GET ${err.symbol} ON PUMP.FUN →` },
        );
        // Don't auto-hide — user needs to click the link. They close the
        // overlay manually via the X button when they're ready to retry.
        return;
      }

      // Special-case: Phantom's active wallet ≠ the one the player is
      // logged into the game with. Caught BEFORE signing → no tx sent,
      // no gas wasted, rewards can't land in a stranger row. Give the
      // user a clear instruction and leave the overlay open so they
      // can switch in Phantom and retry without re-clicking PLAY.
      if (err instanceof WalletMismatchError) {
        overlay.setStatus(
          'Wrong wallet active in Phantom',
          'error',
          `Active: ${err.active.slice(0, 4)}…${err.active.slice(-4)} • Logged in: ${err.loggedIn.slice(0, 4)}…${err.loggedIn.slice(-4)}. Switch accounts in Phantom and try again.`,
        );
        return;
      }

      overlay.setStatus('Payment failed', 'error', msg);
      this.time.delayedCall(3000, () => {
        overlay.hide();
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('MainMenuScene');
        });
      });
    };

    const onCancel = () => {
      overlay.hide();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    };

    // === Dev shortcuts (skip overlay entirely) ===
    if (this.demoMode) {
      this.entryToken = null;
      this.dealCards(cx, deckY, dealText, deckGlow);
      return;
    }
    if (IS_DEV_BUILD && isFreePlayEnabled()) {
      try { onPaid(await getInstantDevToken(playerName)); } catch (err) { onFailure(err); }
      return;
    }
    if (!isPaymentRequired()) {
      try { onPaid(await getDevEntryToken(playerName)); } catch (err) { onFailure(err); }
      return;
    }

    // === Pre-flight: bail if arena is paused for maintenance ===
    // Avoids opening the payment modal with the SNK card silently
    // hidden (quote 503s under maintenance) while USDC stays visible
    // and would lead to a "no active arena" error after sign + send.
    const cfg = await fetchPaymentConfigStatus();
    if (!cfg.acceptingEntries) {
      this.showMaintenanceBanner(cfg.maintenanceMode);
      return;
    }

    // === Real payment: wallet-first, then stake + coin choice, then pay ===
    let quote: PaymentQuote | null = null;
    // Stake multiplier the user has currently picked in the modal. The
    // server is the source of truth — it validates against the on-chain
    // amount at verify time and signs the resolved value into the entry
    // token. We just need to send the same value we quoted with.
    let selectedMultiplier: StakeMultiplier = 1;

    // Build a USDC label like "1 USDC" (x1) or "10 USDC" (x10) by scaling
    // the base entry-fee text with the multiplier. Avoids re-fetching the
    // server quote just to render the user's selection in the modal.
    const usdcLabelFor = (m: number) => {
      const human = (Number(PAYMENT_CONFIG.amount) * m) / 10 ** PAYMENT_CONFIG.decimals;
      return `${human} ${PAYMENT_CONFIG.symbol}`;
    };

    const refreshQuote = async (mult: StakeMultiplier) => {
      quote = await fetchPaymentQuote(mult);
      overlay.setAmounts({
        usdcLabel: usdcLabelFor(mult),
        solLabel: formatLamportsAsSOL(quote.sol.lamports),
      });
      // Native (mainnet only) — server includes a `native` block when the
      // pump.fun bonding curve oracle is reachable. Hide the card when
      // either the cluster is devnet or the oracle errored.
      if (nativeQuoteIsAvailable(quote.native)) {
        const human = Number(quote.native.amount) / 10 ** quote.native.decimals;
        // Trim trailing zeros: small token prices need 4-6 decimals to be
        // legible, but we don't want "1.000000 PMS" cluttering the UI.
        const display = human.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
        overlay.setNativeAvailable(true, {
          symbol: quote.native.symbol,
          label: `${display} ${quote.native.symbol}`,
        });
      } else {
        overlay.setNativeAvailable(false);
      }
    };

    const ensureQuote = async () => {
      if (!quote) await refreshQuote(selectedMultiplier);
    };

    const onStakeChanged = async (mult: StakeMultiplierValue) => {
      selectedMultiplier = mult as StakeMultiplier;
      track('stake_selected', { multiplier: mult });
      overlay.setStakePickerEnabled(false);
      try {
        await refreshQuote(selectedMultiplier);
      } catch (err) {
        console.error('[perk-select] stake quote refresh failed:', err);
      } finally {
        overlay.setStakePickerEnabled(true);
      }
    };

    const runPayment = async (kind: CoinKind) => {
      track('coin_selected', { kind, multiplier: selectedMultiplier });
      overlay.setStep('waiting');
      const stakeNote = selectedMultiplier > 1 ? ` · STAKE x${selectedMultiplier}` : '';
      overlay.setStatus('Preparing transaction…', 'info', `${kind.toUpperCase()} on ${PAYMENT_CONFIG.cluster}${stakeNote}`);
      try {
        // Re-quote right before paying so live amounts (SOL, native) are
        // fresh — same values the server will accept on verify. We always
        // quote with the chosen multiplier so the raw amount matches what
        // we're about to sign for.
        if (kind === 'sol' || kind === 'native') quote = await fetchPaymentQuote(selectedMultiplier);
        const lamports = quote?.sol.lamports ?? PAYMENT_CONFIG.solAmount;

        let signature: string;
        let walletAddress: string;
        if (kind === 'sol') {
          ({ signature, walletAddress } = await payEntryFeeSOL(lamports, (status) => updateStatus(status)));
        } else if (kind === 'native') {
          if (!nativeQuoteIsAvailable(quote?.native)) {
            throw new Error('Native token not available right now (oracle down). Please try USDC or SOL.');
          }
          ({ signature, walletAddress } = await payEntryFeeNative(
            quote.native.amount,
            quote.native.mint,
            quote.native.decimals,
            quote.native.tokenProgram,
            (status) => updateStatus(status),
            quote.native.symbol,
          ));
        } else {
          ({ signature, walletAddress } = await payEntryFee((status) => updateStatus(status), selectedMultiplier));
        }

        overlay.setStatus('Verifying payment…', 'info');
        // Body uses the new `paymentAsset` field for usdc/native; keep the
        // legacy `tokenType` for SOL (server has its own SOL code path).
        const verifyParams: Parameters<typeof verifyAndGetEntryToken>[0] = {
          signature, walletAddress, playerName, multiplier: selectedMultiplier,
        };
        if (kind === 'sol') verifyParams.tokenType = 'sol';
        else verifyParams.paymentAsset = kind; // 'usdc' | 'native'
        const token = await verifyAndGetEntryToken(verifyParams);

        overlay.setStep('success');
        overlay.setStatus('Payment confirmed', 'success');
        track('payment_confirmed', { kind, multiplier: selectedMultiplier });
        this.time.delayedCall(500, () => onPaid(token));
      } catch (err: any) {
        const reasonName =
          err?.name === 'InsufficientNativeBalanceError' ? 'insufficient_native_balance' :
          err?.name === 'WalletMismatchError' ? 'wallet_mismatch' :
          /User rejected|denied|cancel/i.test(err?.message ?? '') ? 'user_cancelled' :
          /not finalized|finalization/i.test(err?.message ?? '') ? 'finalization_timeout' :
          'unknown';
        track('payment_failed', { kind, multiplier: selectedMultiplier, reason: reasonName });
        onFailure(err);
      }
    };

    const updateStatus = (status: 'building' | 'awaiting_signature' | 'confirming' | 'finalizing') => {
      if (status === 'awaiting_signature') overlay.setStatus('Approve in your wallet…', 'info');
      else if (status === 'confirming')    overlay.setStatus('Confirming on-chain…', 'info');
      else if (status === 'finalizing')    overlay.setStatus('Finalizing (irreversible)…', 'info', '~12s — almost done');
    };

    const goToChoose = async () => {
      overlay.setStep('choose');
      await ensureQuote();
    };

    track('payment_modal_opened', { cluster: PAYMENT_CONFIG.cluster });
    overlay.show({
      step: 'connect',
      usdcLabel: formatEntryFee(),
      solLabel: '—',     // populated once the quote resolves
      // Phantom doesn't auto-switch network — surface a banner so devnet
      // testers don't accidentally pay from a Mainnet-configured wallet.
      devnetWarning: PAYMENT_CONFIG.cluster === 'devnet',
      callbacks: {
        onCancel,
        onConnect: async () => {
          overlay.setStatus('Opening wallet…', 'info');
          const res = await connectWallet();
          if (res) {
            await goToChoose();
          } else {
            overlay.setStatus('Wallet not connected', 'error', 'Install Phantom or Solflare, then try again.');
          }
        },
        onCoinChosen: (kind) => { void runPayment(kind); },
        onStakeChanged: (mult) => { void onStakeChanged(mult); },
      },
    });

    // If the user already connected before, jump straight to the coin chooser.
    if (getSavedWallet()) await goToChoose();
  }

  /** Show a full-scene "arena paused for maintenance" message and route
   *  the player back to the main menu after a pause. Used in lieu of
   *  opening the payment modal when /api/payments/config reports
   *  acceptingEntries=false. */
  private showMaintenanceBanner(mode: string): void {
    const { width, height } = this.scale;
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.78).setDepth(1000);
    const title = this.add.text(width / 2, height / 2 - 40, 'ARENA UNDER MAINTENANCE', {
      fontFamily: 'monospace', fontSize: '28px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(1001);
    const subtitle = this.add.text(
      width / 2, height / 2 + 4,
      mode === 'after_current'
        ? 'The current match is wrapping up — no new entries are being accepted right now.'
        : 'New entries are paused right now. Please try again in a moment.',
      { fontFamily: 'monospace', fontSize: '14px', color: '#d8e0ef', align: 'center' },
    ).setOrigin(0.5).setDepth(1001).setWordWrapWidth(width - 80);
    const hint = this.add.text(width / 2, height / 2 + 56, 'Returning to menu…', {
      fontFamily: 'monospace', fontSize: '12px', color: '#93a3bd',
    }).setOrigin(0.5).setDepth(1001);

    // Hold long enough for the player to read the message before the
    // fade kicks in (~7s total).
    this.time.delayedCall(5500, () => {
      this.cameras.main.fadeOut(1500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        dim.destroy(); title.destroy(); subtitle.destroy(); hint.destroy();
        this.scene.start('MainMenuScene');
      });
    });
  }

  /** Lighten a hex color by a factor (0-1) */
  private lightenColor(color: number, factor: number): number {
    let r = (color >> 16) & 0xff;
    let g = (color >> 8) & 0xff;
    let b = color & 0xff;
    r = Math.min(255, Math.floor(r + (255 - r) * factor));
    g = Math.min(255, Math.floor(g + (255 - g) * factor));
    b = Math.min(255, Math.floor(b + (255 - b) * factor));
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Lazy-create a radial vignette texture (transparent center → dark edges).
   * Generated once per session and reused for every card's artwork window.
   */
  private getVignetteTexture(): string {
    const key = 'card_vignette';
    if (this.textures.exists(key)) return key;
    const size = 256;
    const tex = this.textures.createCanvas(key, size, size);
    if (!tex) return key;
    const ctx = tex.getContext();
    const grad = ctx.createRadialGradient(size / 2, size / 2, size * 0.30, size / 2, size / 2, size * 0.55);
    grad.addColorStop(0,    'rgba(0,0,0,0)');
    grad.addColorStop(0.6,  'rgba(0,0,0,0.15)');
    grad.addColorStop(1,    'rgba(0,0,0,0.75)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
    return key;
  }

  /**
   * Lazy-create a small grayscale-noise paper texture, used (with OVERLAY
   * blend) on every card body to break the flat fill into something that
   * reads as cardstock rather than a CSS rectangle.
   */
  private getPaperTexture(): string {
    const key = 'card_paper';
    if (this.textures.exists(key)) return key;
    const size = 128;
    const tex = this.textures.createCanvas(key, size, size);
    if (!tex) return key;
    const ctx = tex.getContext();
    const img = ctx.createImageData(size, size);
    for (let i = 0; i < img.data.length; i += 4) {
      // Centered around mid-gray (128) — OVERLAY brightens above 128, darkens below.
      // ±25 jitter gives a believable paper grain without obvious noise pattern.
      const v = 128 + (Math.random() * 50 - 25);
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    tex.refresh();
    return key;
  }
}
